import { FC, useCallback, useMemo } from 'react';

import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import {
  Document,
  transcriptorApi,
} from '@/shared/api/transcriptor/transcriptorApi';
import { DEFAULT_LOCALE } from '@/shared/config';
import { useAppDispatch, useUserTimezone } from '@/shared/hooks';
import { getName } from '@/shared/lib';

import {
  TranscriptorConsultation,
  transcriptorModel,
} from '@/entities/transcriptor';

import { ConsultationNode, dentalConsultationMessages } from '../../config';
import {
  convertNodesToDocumentUpdate,
  formatDateSafely,
  isDocumentFailedWithoutContent,
  isDocumentGenerating,
  isEmptyField,
  parseSummaryData,
} from '../../lib';
import { AbstractBlock } from '../AbstractBlock/AbstractBlock';
import { ConsultationEmptyState } from '../ConsultationEmptyState/ConsultationEmptyState';
import { ConsultationWidget } from '../ConsultationWidget/ConsultationWidget';

type SummaryWidgetProps = {
  consultation?: TranscriptorConsultation | null;
  summary?: Document | null;
  patient?: Patient | null;
  authorName?: string;
  userLocale?: string;
  onRegenerate?: () => void;
};

export const SummaryWidget: FC<SummaryWidgetProps> = ({
  consultation,
  summary,
  patient,
  authorName,
  userLocale = DEFAULT_LOCALE,
  onRegenerate,
}) => {
  const dispatch = useAppDispatch();
  const userTimezone = useUserTimezone();

  const consultationId = consultation?.id;
  const patientId = consultation?.patientId;
  const status = consultation?.status;
  const summaryId = summary?.id;
  const documentStatus = summary?.status;

  const enrichedData = useMemo(() => {
    if (!summary) {
      return [];
    }

    const data = parseSummaryData(summary);

    if (!patient) {
      return data;
    }

    return data.map((node) => ({
      ...node,
      children: node.children.map((section) => {
        if (
          (section.title === 'Patient Name' ||
            section.category === 'patient_name') &&
          isEmptyField(section)
        ) {
          const patientName = getName(
            patient.PersonalData?.FirstName,
            patient.PersonalData?.LastName,
            { userLocale },
          );
          return {
            ...section,
            content: [{ text: patientName || '' }],
          };
        }

        if (
          (section.title === 'Patient Date of Birth' ||
            section.category === 'patient_dob') &&
          isEmptyField(section)
        ) {
          const dob = formatDateSafely(
            patient.PersonalData?.DateOfBirth,
            userLocale,
            userTimezone,
          );
          return {
            ...section,
            content: [{ text: dob }],
          };
        }

        if (
          (section.title === 'Doctor Name' ||
            section.category === 'doctor_name') &&
          isEmptyField(section) &&
          authorName
        ) {
          return {
            ...section,
            content: [{ text: authorName }],
          };
        }

        return section;
      }),
    }));
  }, [summary, patient, authorName, userLocale, userTimezone]);

  const handleSave = useCallback(
    async (editedData: ConsultationNode[]) => {
      if (!consultationId) {
        throw new Error('Consultation ID is missing');
      }

      if (!summaryId) {
        throw new Error('Summary document is not yet generated');
      }

      const updateRequest = convertNodesToDocumentUpdate(editedData);
      await transcriptorApi.updateConsultationDocument(
        consultationId,
        summaryId,
        updateRequest,
      );

      // Refetch consultation to get updated data
      dispatch(
        transcriptorModel.thunks.fetchConsultationById({
          consultationId,
          patientId,
        }),
      );
    },
    [consultationId, patientId, summaryId],
  );

  return (
    <>
      <AbstractBlock abstract={consultation?.abstract} />

      {!summary ? (
        <ConsultationEmptyState
          status={status}
          noDataMessage={dentalConsultationMessages.noSummaryAvailable}
          onRegenerate={onRegenerate}
        />
      ) : documentStatus === 'skipped' ||
        isDocumentGenerating(summary) ||
        isDocumentFailedWithoutContent(summary) ? (
        <ConsultationEmptyState
          status={status}
          documentStatus={documentStatus}
          documentError={summary.error}
          noDataMessage={dentalConsultationMessages.noSummaryAvailable}
          onRegenerate={onRegenerate}
        />
      ) : (
        <ConsultationWidget
          data={enrichedData}
          showActions
          widgetType="summary"
          transcriptChunks={consultation?.transcriptChunks}
          onSave={handleSave}
        />
      )}
    </>
  );
};
